// Sprint 2 PR-D2 — Central Brain Lead Analyses view (read-only, card layout).
// Endpoint: GET /api/v2/lead-analyses

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

export interface LeadAnalysisRow {
  id: string;
  tenantId: string;
  leadId: string;
  intent: string | null;
  score: 'cold' | 'warm' | 'hot' | null;
  summary: string | null;
  missingFields: string[];
  nextAction: string | null;
  suggestedReply: string | null;
  suggestedTask: string | null;
  quoteDraftId: string | null;
  handoffRequired: boolean;
  agentRunId: string | null;
  createdAt: string;
}

export interface LeadAnalysesSummary { total: number; byScore: Record<string, number>; byIntent: Record<string, number>; }
interface LeadAnalysesResponse { ok: true; leadAnalyses: LeadAnalysisRow[]; summary: LeadAnalysesSummary; }
export interface LeadAnalysesFilters { score?: string; intent?: string; leadId?: string; agentRunId?: string; tenantId?: string; limit?: number; }

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; leadAnalyses: LeadAnalysisRow[]; summary: LeadAnalysesSummary; filters: LeadAnalysesFilters }
  | { kind: 'error'; message: string; filters: LeadAnalysesFilters };

let state: ViewState = { kind: 'idle' };
let filters: LeadAnalysesFilters = { limit: 100 };

export async function loadCbLeadAnalyses(): Promise<void> {
  state = { kind: 'loading' };
  const r = await apiGetJson<LeadAnalysesResponse>('/api/v2/lead-analyses', filters as Record<string, string | number | undefined>);
  if (r.ok) state = { kind: 'ready', leadAnalyses: r.data.leadAnalyses, summary: r.data.summary, filters: { ...filters } };
  else state = { kind: 'error', message: r.message ?? r.error, filters: { ...filters } };
}

export function renderCbLeadAnalyses(): void {
  const root = document.getElementById('view-cb-lead-analyses');
  if (!root) return;
  root.innerHTML = renderCbLeadAnalysesHtml(state);
  attachHandlers();
}

export function renderCbLeadAnalysesHtml(s: ViewState): string {
  const f = (s.kind === 'ready' || s.kind === 'error') ? s.filters : filters;
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Lead Analyses</div>
        <div class="view-sub">central_brain.lead_analyses — AI analiz çıktıları (read-only)</div>
      </div>
    </div>
    <div class="rt-card" style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
      ${selectFilter('score', 'Score', f.score, ['cold','warm','hot'])}
      ${inputFilter('intent', 'Intent', f.intent)}
      ${inputFilter('leadId', 'Lead ID', f.leadId)}
      ${inputFilter('tenantId', 'Tenant', f.tenantId)}
      <button id="cbla-apply" class="btn-primary" style="padding:6px 12px;">Filtrele</button>
    </div>`;
  if (s.kind === 'idle' || s.kind === 'loading') return `${header}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  if (s.kind === 'error') return `${header}<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(s.message)}</div></div>`;
  const { leadAnalyses, summary } = s;
  return `${header}
    <div class="rt-card" style="display:flex;flex-wrap:wrap;gap:14px;font-size:12px;">
      <span><strong>${summary.total}</strong> total</span>
      ${chips('score', summary.byScore)}
      ${chips('intent', summary.byIntent)}
    </div>
    ${leadAnalyses.length === 0
      ? '<div class="empty-state"><div class="empty-icon">📭</div><div class="empty-title">Eşleşen lead analysis yok</div></div>'
      : `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(360px,1fr));gap:12px;">
          ${leadAnalyses.map(la => renderCard(la)).join('')}
        </div>`
    }`;
}

function renderCard(la: LeadAnalysisRow): string {
  const scoreColor = la.score === 'hot' ? '#e55' : la.score === 'warm' ? '#fb0' : la.score === 'cold' ? '#88f' : '#888';
  return `<div class="rt-card">
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
      <span style="background:${scoreColor};color:white;padding:3px 10px;border-radius:12px;font-size:11px;font-weight:600;text-transform:uppercase;">${escHtml(la.score ?? '—')}</span>
      <span style="font-size:11px;color:var(--muted);">${escHtml(la.intent ?? '—')}</span>
      <span style="margin-left:auto;font-size:10px;color:var(--muted);">${escHtml(la.createdAt)}</span>
    </div>
    <div style="font-weight:600;font-size:12px;margin-bottom:4px;">Lead: <code>${escHtml(la.leadId)}</code></div>
    ${la.summary ? `<div style="font-size:13px;margin-bottom:8px;">${escHtml(la.summary)}</div>` : ''}
    ${la.missingFields.length > 0 ? `<div style="margin-bottom:8px;"><span style="font-size:10px;color:var(--muted);text-transform:uppercase;">missing:</span> ${la.missingFields.map(f => `<code style="background:var(--bg2);padding:1px 6px;border-radius:4px;font-size:10px;margin-right:3px;">${escHtml(f)}</code>`).join('')}</div>` : ''}
    ${la.suggestedReply ? `<div style="background:var(--bg2);padding:8px;border-radius:4px;font-size:12px;margin-bottom:6px;"><span style="color:var(--muted);font-size:10px;text-transform:uppercase;">suggested reply:</span><br>${escHtml(la.suggestedReply)}</div>` : ''}
    ${la.nextAction ? `<div style="font-size:11px;color:var(--muted);"><strong>next:</strong> ${escHtml(la.nextAction)}</div>` : ''}
    ${la.handoffRequired ? '<div style="margin-top:6px;font-size:11px;color:var(--warn,#fb0);">⚠️ handoff required</div>' : ''}
  </div>`;
}

function chips(label: string, m: Record<string, number>): string {
  return `<span><span style="color:var(--muted);">${escHtml(label)}:</span> ${Object.entries(m).map(([k,v]) => `<span style="background:var(--bg2);padding:2px 6px;border-radius:8px;margin-right:4px;">${escHtml(k)} <strong>${v}</strong></span>`).join('')}</span>`;
}
function selectFilter(id: string, label: string, value: string | undefined, opts: string[]): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<select id="cbla-f-${id}" class="form-select" style="width:120px;padding:4px 8px;"><option value="">(any)</option>${opts.map(o => `<option value="${escHtml(o)}"${o===value?' selected':''}>${escHtml(o)}</option>`).join('')}</select></label>`;
}
function inputFilter(id: string, label: string, value: string | undefined): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<input id="cbla-f-${id}" class="form-input" value="${escHtml(value ?? '')}" style="width:140px;padding:4px 8px;" /></label>`;
}
function attachHandlers(): void {
  const btn = document.getElementById('cbla-apply');
  if (!btn) return;
  btn.addEventListener('click', async () => {
    const getV = (id: string) => (document.getElementById(`cbla-f-${id}`) as HTMLInputElement | HTMLSelectElement | null)?.value || undefined;
    filters = { score: getV('score'), intent: getV('intent'), leadId: getV('leadId'), tenantId: getV('tenantId'), limit: 100 };
    await loadCbLeadAnalyses();
    renderCbLeadAnalyses();
  });
}
